import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import '@babylonjs/core/Meshes/instancedMesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateGround } from '@babylonjs/core/Meshes/Builders/groundBuilder';
import { CreatePolyhedron } from '@babylonjs/core/Meshes/Builders/polyhedronBuilder';
import { CreateTorus } from '@babylonjs/core/Meshes/Builders/torusBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import type { Scene } from '@babylonjs/core/scene';
import { GRID_CELL_SIZE } from '../config/GameConfig';
import { GridNavigator, type NavObstacle } from '../navigation/GridNavigator';
import type { LevelDefinition, RuinPiece } from './LevelTypes';

interface ElevationZone { type: 'flat' | 'ramp'; x: number; z: number; width: number; depth: number; height: number; rotation: number }

export class LevelBuilder {
  readonly root: TransformNode;
  readonly navigator: GridNavigator;
  private readonly obstacleBounds: NavObstacle[] = [];
  private readonly elevations: ElevationZone[] = [];
  private readonly stone: StandardMaterial;
  private readonly stoneLight: StandardMaterial;
  private readonly stoneDark: StandardMaterial;
  private readonly moss: StandardMaterial;
  private readonly leaf: StandardMaterial;
  private readonly wood: StandardMaterial;
  private readonly glow: StandardMaterial;
  private dust?: ParticleSystem;

  constructor(private readonly scene: Scene, readonly definition: LevelDefinition, particleCapacity: number) {
    this.root = new TransformNode('level-root', scene);
    this.stone = this.makeMaterial('warm-sandstone', '#B87948');
    this.stoneLight = this.makeMaterial('sunlit-stone', '#D6A15F');
    this.stoneDark = this.makeMaterial('stone-shadow', '#74513F');
    this.moss = this.makeMaterial('soft-moss', '#3E7650');
    this.leaf = this.makeMaterial('jungle-leaves', '#186C62');
    this.wood = this.makeMaterial('tree-bark', '#684632');
    this.glow = this.makeMaterial('rune-glow', '#FFC95C', '#8A4C10');

    this.createGround();
    definition.pieces.forEach((piece, index) => this.createPiece(piece, index));
    this.navigator = new GridNavigator(definition.width, definition.depth, GRID_CELL_SIZE, this.obstacleBounds);
    if (particleCapacity > 0) this.createDust(particleCapacity);
  }

  floorHeightAt(x: number, z: number): number {
    let height = 0;
    for (const zone of this.elevations) {
      const cos = Math.cos(-zone.rotation);
      const sin = Math.sin(-zone.rotation);
      const dx = x - zone.x;
      const dz = z - zone.z;
      const lx = dx * cos - dz * sin;
      const lz = dx * sin + dz * cos;
      if (Math.abs(lx) > zone.width / 2 || Math.abs(lz) > zone.depth / 2) continue;
      if (zone.type === 'flat') height = Math.max(height, zone.height);
      else height = Math.max(height, ((lz / zone.depth) + 0.5) * zone.height);
    }
    return height;
  }

  dispose(): void {
    this.dust?.dispose();
    this.root.dispose(false, true);
  }

  private createGround(): void {
    const ground = CreateGround('courtyard-floor', {
      width: this.definition.width + 8,
      height: this.definition.depth + 8,
      subdivisions: 2,
    }, this.scene);
    ground.material = this.stoneDark;
    ground.receiveShadows = true;
    ground.checkCollisions = false;
    ground.parent = this.root;

    const tile = CreateBox('floor-tile-source', { width: 2.8, depth: 2.8, height: 0.11, wrap: true }, this.scene);
    tile.material = this.stone;
    tile.position.y = 0.02;
    tile.parent = this.root;
    let tileIndex = 0;
    for (let z = -this.definition.depth / 2 + 1.5; z < this.definition.depth / 2; z += 3) {
      for (let x = -this.definition.width / 2 + 1.5; x < this.definition.width / 2; x += 3) {
        const instance = tileIndex++ === 0 ? tile : tile.createInstance(`floor-tile-${tileIndex}`);
        instance.position.set(x + Math.sin(x * 9 + z) * 0.08, 0.02 + Math.sin(x * 3.1 + z) * 0.012, z);
        instance.rotation.y = Math.sin(x * 2 + z * 1.7) * 0.025;
        instance.scaling.set(0.94 + ((tileIndex * 17) % 5) * 0.01, 1, 0.94 + ((tileIndex * 11) % 6) * 0.01);
        instance.freezeWorldMatrix();
      }
    }

    const outer = CreateGround('outer-jungle', { width: this.definition.width + 70, height: this.definition.depth + 70 }, this.scene);
    outer.position.y = -0.14;
    outer.material = this.moss;
    outer.parent = this.root;
    outer.freezeWorldMatrix();
  }

  private createPiece(piece: RuinPiece, index: number): void {
    switch (piece.type) {
      case 'wall': this.createWall(piece, index, false); break;
      case 'broken-wall': this.createWall(piece, index, true); break;
      case 'column': this.createColumn(piece, index); break;
      case 'arch': this.createArch(piece, index); break;
      case 'tunnel': this.createTunnel(piece, index); break;
      case 'rubble': this.createRubble(piece, index); break;
      case 'tree': this.createTree(piece, index); break;
      case 'stairs': this.createStairs(piece, index); break;
      case 'platform': this.createPlatform(piece, index); break;
      case 'statue': this.createStatue(piece, index); break;
    }
  }

  private createWall(piece: RuinPiece, index: number, broken: boolean): void {
    const width = piece.width ?? 5;
    const depth = piece.depth ?? 1.2;
    const height = piece.height ?? 3;
    const node = new TransformNode(`ruined-wall-${index}`, this.scene);
    node.position.set(piece.x, 0, piece.z);
    node.rotation.y = piece.rotation ?? 0;
    node.parent = this.root;
    const courses = Math.max(2, Math.ceil(height / 0.72));
    const blocks = Math.max(2, Math.ceil(width / 1.5));
    for (let row = 0; row < courses; row += 1) {
      const count = broken && row === courses - 1 ? Math.max(1, blocks - 2) : blocks;
      for (let col = 0; col < count; col += 1) {
        const blockWidth = width / blocks - 0.055;
        const block = CreateBox(`wall-block-${index}-${row}-${col}`, {
          width: blockWidth, height: 0.68, depth: depth * (0.92 + ((col + row) % 3) * 0.035),
        }, this.scene);
        block.position.set(-width / 2 + blockWidth / 2 + col * width / blocks + (row % 2) * 0.12, 0.36 + row * 0.7, 0);
        block.rotation.z = Math.sin(index * 31 + row * 7 + col) * 0.018;
        block.material = (row + col) % 4 === 0 ? this.stoneLight : this.stone;
        block.receiveShadows = true;
        block.parent = node;
      }
    }
    const mossCap = CreateBox(`wall-moss-${index}`, { width: width * 0.72, height: 0.08, depth: depth * 1.03 }, this.scene);
    mossCap.position.set(-width * 0.08, Math.min(height, courses * 0.7) + 0.02, 0);
    mossCap.material = this.moss;
    mossCap.parent = node;
    this.addObstacle(piece.x, piece.z, width, depth, piece.rotation ?? 0, node, height);
  }

  private createColumn(piece: RuinPiece, index: number): void {
    const height = piece.height ?? 4.8;
    const node = new TransformNode(`carved-column-${index}`, this.scene);
    node.position.set(piece.x, 0, piece.z);
    node.parent = this.root;
    const base = CreateCylinder(`column-base-${index}`, { height: 0.45, diameter: 1.55, tessellation: 8 }, this.scene);
    base.position.y = 0.225; base.material = this.stoneDark; base.parent = node;
    const plinth = CreateCylinder(`column-plinth-${index}`, { height: 0.35, diameterTop: 1.25, diameterBottom: 1.48, tessellation: 8 }, this.scene);
    plinth.position.y = 0.58; plinth.material = this.stone; plinth.parent = node;
    const shaft = CreateCylinder(`column-shaft-${index}`, { height: height - 1.1, diameterTop: 0.88, diameterBottom: 1.08, tessellation: 12 }, this.scene);
    shaft.position.y = height / 2; shaft.material = this.stoneLight; shaft.parent = node; shaft.receiveShadows = true;
    for (let band = 0; band < 3; band += 1) {
      const torus = CreateTorus(`column-band-${index}-${band}`, { diameter: 1.02, thickness: 0.11, tessellation: 12 }, this.scene);
      torus.rotation.x = Math.PI / 2; torus.position.y = 1 + band * (height - 1.5) / 2; torus.material = this.stoneDark; torus.parent = node;
    }
    const capital = CreateCylinder(`column-capital-${index}`, { height: 0.48, diameterTop: 1.58, diameterBottom: 1.1, tessellation: 8 }, this.scene);
    capital.position.y = height - 0.25; capital.material = this.stone; capital.parent = node;
    this.addObstacle(piece.x, piece.z, 1.45, 1.45, 0, node, height);
  }

  private createArch(piece: RuinPiece, index: number): void {
    const node = new TransformNode(`sun-arch-${index}`, this.scene);
    node.position.set(piece.x, 0, piece.z);
    node.rotation.y = piece.rotation ?? 0;
    node.parent = this.root;
    for (const x of [-1.65, 1.65]) {
      const pillar = CreateBox(`arch-pillar-${index}`, { width: 0.9, height: 3.2, depth: 1.05 }, this.scene);
      pillar.position.set(x, 1.6, 0); pillar.material = this.stone; pillar.parent = node; pillar.checkCollisions = true;
      pillar.metadata = { isObstacle: true };
    }
    const arc = this.createArc(`arch-arc-${index}`, 1.72, 0.62, 1.05);
    arc.position.y = 3.15; arc.material = this.stoneLight; arc.parent = node;
    const rune = CreatePolyhedron(`arch-rune-${index}`, { type: 1, size: 0.24 }, this.scene);
    rune.position.set(0, 4.68, 0.57); rune.material = this.glow; rune.parent = node;
    const cos = Math.cos(piece.rotation ?? 0); const sin = Math.sin(piece.rotation ?? 0);
    for (const localX of [-1.65, 1.65]) this.addObstacle(piece.x + localX * cos, piece.z - localX * sin, 0.9, 1.05, piece.rotation ?? 0, undefined, 3.2);
  }

  private createArc(name: string, radius: number, thickness: number, depth: number): Mesh {
    const positions: number[] = [];
    const indices: number[] = [];
    const normals: number[] = [];
    const segments = 18;
    for (let side = 0; side < 2; side += 1) {
      const z = (side ? 0.5 : -0.5) * depth;
      for (let i = 0; i <= segments; i += 1) {
        const angle = Math.PI - i * Math.PI / segments;
        for (const r of [radius - thickness / 2, radius + thickness / 2]) {
          positions.push(Math.cos(angle) * r, Math.sin(angle) * r, z);
          normals.push(0, 0, side ? 1 : -1);
        }
      }
    }
    const row = (segments + 1) * 2;
    for (let side = 0; side < 2; side += 1) {
      const offset = side * row;
      for (let i = 0; i < segments; i += 1) {
        const a = offset + i * 2; const b = a + 1; const c = a + 2; const d = a + 3;
        indices.push(a, side ? c : b, side ? b : c, b, side ? c : d, side ? d : c);
      }
    }
    for (let i = 0; i <= segments; i += 1) {
      const f0 = i * 2; const f1 = f0 + 1; const b0 = row + f0; const b1 = row + f1;
      indices.push(f0, b0, f1, f1, b0, b1);
    }
    const mesh = new Mesh(name, this.scene);
    const data = new VertexData();
    data.positions = positions; data.indices = indices;
    VertexData.ComputeNormals(positions, indices, normals);
    data.normals = normals; data.applyToMesh(mesh);
    return mesh;
  }

  private createTunnel(piece: RuinPiece, index: number): void {
    const width = piece.width ?? 5; const depth = piece.depth ?? 8; const height = piece.height ?? 3.6;
    const rotation = piece.rotation ?? 0;
    const node = new TransformNode(`arched-tunnel-${index}`, this.scene);
    node.position.set(piece.x, 0, piece.z); node.rotation.y = rotation; node.parent = this.root;
    for (const side of [-1, 1]) {
      const sideWall = CreateBox(`tunnel-side-${index}-${side}`, { width: 0.75, height: height, depth }, this.scene);
      sideWall.position.set(side * (width / 2 - 0.38), height / 2, 0); sideWall.material = this.stoneDark; sideWall.parent = node;
      for (let step = 0; step < 4; step += 1) {
        const buttress = CreateBox(`tunnel-buttress-${index}-${side}-${step}`, { width: 1.02, height: height * 0.72, depth: 0.42 }, this.scene);
        buttress.position.set(side * (width / 2 - 0.3), height * 0.36, -depth / 2 + 0.8 + step * (depth - 1.6) / 3);
        buttress.material = this.stone; buttress.parent = node;
      }
    }
    for (let segment = 0; segment < 5; segment += 1) {
      const roof = CreateBox(`tunnel-roof-${index}-${segment}`, { width: width - 0.7, height: 0.48, depth: depth / 5 + 0.08 }, this.scene);
      roof.position.set(0, height + Math.sin(segment / 4 * Math.PI) * 0.32, -depth / 2 + (segment + 0.5) * depth / 5);
      roof.rotation.z = (segment - 2) * 0.055; roof.material = segment % 2 ? this.stoneLight : this.stone; roof.parent = node;
    }
    const cos = Math.cos(rotation); const sin = Math.sin(rotation);
    for (const side of [-1, 1]) {
      const localX = side * (width / 2 - 0.38);
      this.addObstacle(piece.x + localX * cos, piece.z - localX * sin, 0.75, depth, rotation, undefined, height);
    }
  }

  private createRubble(piece: RuinPiece, index: number): void {
    const node = new TransformNode(`friendly-rubble-${index}`, this.scene);
    node.position.set(piece.x, 0, piece.z); node.parent = this.root;
    const width = piece.width ?? 2; const depth = piece.depth ?? 2;
    for (let i = 0; i < 7; i += 1) {
      const rock = CreatePolyhedron(`rubble-rock-${index}-${i}`, { type: i % 2, size: 0.45 + (i % 3) * 0.18 }, this.scene);
      rock.scaling.set(1.2, 0.65 + (i % 2) * 0.25, 1);
      rock.position.set(Math.sin(i * 4.7) * width * 0.34, 0.25 + (i % 3) * 0.08, Math.cos(i * 3.2) * depth * 0.34);
      rock.rotation.set(i * 0.4, i * 1.7, i * 0.2); rock.material = i % 3 ? this.stone : this.stoneDark; rock.parent = node;
    }
    this.addObstacle(piece.x, piece.z, width * 0.7, depth * 0.7, 0, undefined, 1);
  }

  private createTree(piece: RuinPiece, index: number): void {
    const node = new TransformNode(`ruin-tree-${index}`, this.scene);
    node.position.set(piece.x, 0, piece.z); node.parent = this.root;
    const trunk = CreateCylinder(`tree-trunk-${index}`, { height: 3.8, diameterTop: 0.42, diameterBottom: 0.72, tessellation: 7 }, this.scene);
    trunk.position.y = 1.9; trunk.rotation.z = Math.sin(index * 8) * 0.08; trunk.material = this.wood; trunk.parent = node;
    for (let i = 0; i < 6; i += 1) {
      const crown = CreatePolyhedron(`tree-crown-${index}-${i}`, { type: 2, size: 1.35 - (i % 2) * 0.15 }, this.scene);
      crown.position.set(Math.sin(i * 2.4) * 1.1, 3.7 + (i % 3) * 0.65, Math.cos(i * 2.4) * 1.1);
      crown.scaling.y = 0.75; crown.material = i % 2 ? this.leaf : this.moss; crown.parent = node;
    }
    this.addObstacle(piece.x, piece.z, 1.1, 1.1, 0, trunk, 3.8);
  }

  private createStairs(piece: RuinPiece, index: number): void {
    const node = new TransformNode(`stone-stairs-${index}`, this.scene);
    node.position.set(piece.x, 0, piece.z); node.rotation.y = piece.rotation ?? 0; node.parent = this.root;
    const steps = 6; const width = piece.width ?? 4; const depth = piece.depth ?? 4.8; const height = piece.height ?? 1.6;
    for (let i = 0; i < steps; i += 1) {
      const step = CreateBox(`stair-${index}-${i}`, { width, depth: depth / steps + 0.05, height: (i + 1) * height / steps }, this.scene);
      step.position.set(0, (i + 1) * height / steps / 2, -depth / 2 + (i + 0.5) * depth / steps);
      step.material = i % 2 ? this.stone : this.stoneLight; step.parent = node;
    }
    this.elevations.push({ type: 'ramp', x: piece.x, z: piece.z, width, depth, height, rotation: piece.rotation ?? 0 });
  }

  private createPlatform(piece: RuinPiece, index: number): void {
    const width = piece.width ?? 6; const depth = piece.depth ?? 6; const height = piece.height ?? 1;
    const base = CreateBox(`raised-platform-${index}`, { width, depth, height }, this.scene);
    base.position.set(piece.x, height / 2, piece.z); base.material = this.stoneDark; base.parent = this.root;
    const top = CreateBox(`platform-cap-${index}`, { width: width + 0.3, depth: depth + 0.3, height: 0.18 }, this.scene);
    top.position.set(piece.x, height + 0.05, piece.z); top.material = this.stoneLight; top.parent = this.root;
    this.elevations.push({ type: 'flat', x: piece.x, z: piece.z, width, depth, height, rotation: 0 });
  }

  private createStatue(piece: RuinPiece, index: number): void {
    const base = CreateCylinder(`statue-base-${index}`, { height: 0.8, diameter: 2.2, tessellation: 8 }, this.scene);
    base.position.set(piece.x, 0.4, piece.z); base.material = this.stoneDark; base.parent = this.root;
    const body = CreateCylinder(`statue-body-${index}`, { height: 2.8, diameterTop: 0.7, diameterBottom: 1.3, tessellation: 8 }, this.scene);
    body.position.set(piece.x, 2.1, piece.z); body.material = this.stone; body.parent = this.root;
    this.addObstacle(piece.x, piece.z, 2, 2, 0, undefined, 3.5);
  }

  private createDust(capacity: number): void {
    const texture = new DynamicTexture('dust-texture', 32, this.scene, false);
    const context = texture.getContext();
    context.clearRect(0, 0, 32, 32);
    const gradient = context.createRadialGradient(16, 16, 1, 16, 16, 15);
    gradient.addColorStop(0, 'rgba(255,232,164,0.9)'); gradient.addColorStop(1, 'rgba(255,232,164,0)');
    context.fillStyle = gradient; context.fillRect(0, 0, 32, 32); texture.update();
    this.dust = new ParticleSystem('golden-dust', capacity, this.scene);
    this.dust.particleTexture = texture;
    this.dust.emitter = new Vector3(0, 2.5, 0);
    this.dust.minEmitBox = new Vector3(-this.definition.width / 2, 0, -this.definition.depth / 2);
    this.dust.maxEmitBox = new Vector3(this.definition.width / 2, 8, this.definition.depth / 2);
    this.dust.color1 = new Color4(1, 0.82, 0.38, 0.7); this.dust.color2 = new Color4(0.5, 0.9, 0.75, 0.35);
    this.dust.minSize = 0.025; this.dust.maxSize = 0.11; this.dust.minLifeTime = 4; this.dust.maxLifeTime = 9;
    this.dust.emitRate = capacity / 12; this.dust.direction1 = new Vector3(-0.08, 0.02, -0.08); this.dust.direction2 = new Vector3(0.08, 0.12, 0.08);
    this.dust.start();
  }

  private addObstacle(x: number, z: number, width: number, depth: number, rotation: number, collider?: Mesh | TransformNode, height = 3): void {
    const cos = Math.abs(Math.cos(rotation)); const sin = Math.abs(Math.sin(rotation));
    const aabbWidth = width * cos + depth * sin; const aabbDepth = width * sin + depth * cos;
    this.obstacleBounds.push({ minX: x - aabbWidth / 2, maxX: x + aabbWidth / 2, minZ: z - aabbDepth / 2, maxZ: z + aabbDepth / 2 });
    if (collider instanceof Mesh) {
      collider.checkCollisions = true; collider.metadata = { ...(collider.metadata ?? {}), isObstacle: true };
    } else if (!collider) {
      const box = CreateBox('navigation-collider', { width, depth, height }, this.scene);
      box.position.set(x, height / 2, z); box.rotation.y = rotation; box.isVisible = false; box.checkCollisions = true; box.isPickable = true;
      box.metadata = { isObstacle: true }; box.parent = this.root;
    } else {
      collider.getChildMeshes().forEach((mesh) => { mesh.checkCollisions = true; mesh.metadata = { ...(mesh.metadata ?? {}), isObstacle: true }; });
    }
  }

  private makeMaterial(name: string, diffuse: string, emissive?: string): StandardMaterial {
    const mat = new StandardMaterial(name, this.scene);
    mat.diffuseColor = Color3.FromHexString(diffuse);
    mat.specularColor = new Color3(0.09, 0.08, 0.06);
    if (emissive) mat.emissiveColor = Color3.FromHexString(emissive);
    return mat;
  }
}
